/**
 * Admin — Coaching Reports index.
 *
 * Lives at /dashboard/admin/coaching-reports. One row per individual
 * (PRI) deployment that has a frozen coaching-report snapshot; the
 * Download button pulls the exact PDF the client received on the last
 * send.
 *
 * Access: super_admin + consultant (org-scoped for consultants).
 */
import React from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return iso;
  }
}

function formatBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

export default function AdminCoachingReports() {
  const { logout } = useAuth();
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [query, setQuery] = React.useState("");
  const [downloadingId, setDownloadingId] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/api/admin/coaching-reports");
        setRows(data.rows || []);
      } catch (e) {
        setError(formatApiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.client_name, r.coach_name, r.org_name, r.deployment_name]
        .filter(Boolean)
        .some((s) => String(s).toLowerCase().includes(q)),
    );
  }, [rows, query]);

  async function handleDownload(row) {
    setDownloadingId(row.snapshot_id);
    setError("");
    try {
      const res = await api.get(row.download_url, { responseType: "blob" });
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="([^"]+)"/.exec(cd);
      const fname = match ? match[1] : row.filename || "coaching-report.pdf";
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setDownloadingId(null);
    }
  }

  return (
    <div className="min-h-screen bg-paper" data-testid="admin-coaching-reports-page">
      <header className="border-b border-gray-100 sticky top-0 z-10 bg-paper">
        <div className="max-w-7xl mx-auto px-8 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="wordmark text-sm">
              <span style={{ color: "#1A1A1A" }}>RESILIENT</span>{" "}
              <span style={{ color: "#2F6B7C" }}>TEAMS</span>
            </span>
            <span className="eyebrow" style={{ color: "#6F6F6F" }}>
              · Admin · Coaching reports
            </span>
          </div>
          <div className="flex items-center gap-6">
            <Link
              to="/dashboard"
              className="btn-quiet"
              style={{ textDecoration: "none" }}
              data-testid="back-to-dashboard"
            >
              ← Home
            </Link>
            <button onClick={logout} className="btn-quiet" data-testid="logout-button">
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-12">
        <h1
          className="text-2xl mb-2"
          style={{ fontFamily: "Oswald", fontWeight: 700, color: "#1A1A1A" }}
        >
          Coaching reports.
        </h1>
        <p className="text-sm text-gray-600 mb-8 max-w-prose">
          Every individual (PRI) engagement whose report has been delivered.
          The download returns the byte-exact PDF that was last emailed to the
          client — the record of what they actually received.
        </p>

        <div className="flex items-center gap-3 mb-6">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by client, coach, org…"
            data-testid="admin-coaching-reports-filter"
            className="text-sm px-3 py-2"
            style={{
              border: "1px solid #E0E0E0",
              background: "#FFFFFF",
              minWidth: "320px",
              fontFamily: "Georgia, serif",
            }}
          />
          <span className="text-xs" style={{ color: "#6F6F6F" }}>
            {filtered.length} of {rows.length}
          </span>
        </div>

        {error && (
          <div
            className="text-sm mb-4"
            style={{ color: "#AD393E" }}
            data-testid="admin-coaching-reports-error"
          >
            {error}
          </div>
        )}

        {loading && (
          <div className="text-sm text-gray-500" data-testid="admin-coaching-reports-loading">
            Loading…
          </div>
        )}

        {!loading && rows.length === 0 && !error && (
          <div
            className="text-sm text-gray-500 mt-6"
            data-testid="admin-coaching-reports-empty"
          >
            No coaching reports have been sent yet. Once a coach clicks
            &ldquo;Send to client&rdquo; on an individual deployment, the frozen
            PDF will appear here.
          </div>
        )}

        {!loading && filtered.length > 0 && (
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm"
              data-testid="admin-coaching-reports-table"
            >
              <thead>
                <tr style={{ background: "#FBFAF7" }}>
                  {["Client", "Coach", "Organization", "Sent", "Version", "Size", ""].map(
                    (h) => (
                      <th
                        key={h}
                        className="text-left py-2 px-3"
                        style={{
                          fontFamily: "Oswald",
                          fontSize: "11px",
                          letterSpacing: "0.08em",
                          textTransform: "uppercase",
                          color: "#6F6F6F",
                        }}
                      >
                        {h}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => (
                  <tr
                    key={r.snapshot_id}
                    data-testid="admin-coaching-reports-row"
                    style={{ borderBottom: "1px solid #EEE" }}
                  >
                    <td className="py-3 px-3">
                      <div style={{ fontWeight: 600, color: "#1A1A1A" }}>
                        {r.client_name || "—"}
                      </div>
                      <div className="text-xs text-gray-500">
                        {r.deployment_name}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div>{r.coach_name}</div>
                      {r.coach_email && (
                        <div className="text-xs text-gray-500">{r.coach_email}</div>
                      )}
                    </td>
                    <td className="py-3 px-3 text-gray-700">{r.org_name}</td>
                    <td className="py-3 px-3 text-xs text-gray-700">
                      {formatDate(r.sent_at)}
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-700">
                      v{r.version}
                    </td>
                    <td className="py-3 px-3 text-xs text-gray-500">
                      {formatBytes(r.byte_size)}
                    </td>
                    <td className="py-3 px-3">
                      <button
                        onClick={() => handleDownload(r)}
                        disabled={downloadingId === r.snapshot_id}
                        data-testid="admin-coaching-reports-download"
                        style={{
                          fontFamily: "Oswald",
                          letterSpacing: "0.18em",
                          textTransform: "uppercase",
                          fontSize: "11px",
                          color:
                            downloadingId === r.snapshot_id ? "#999898" : "#2F6B7C",
                          border: `1px solid ${
                            downloadingId === r.snapshot_id ? "#D0D0D0" : "#4592A8"
                          }`,
                          background: "transparent",
                          padding: "6px 12px",
                          cursor:
                            downloadingId === r.snapshot_id
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
                        {downloadingId === r.snapshot_id ? "Preparing…" : "Download"}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
